import React from 'react';
import cn from 'classnames';
import type { CommentAttachment } from './types';
import '../../_organisms/Timeline/commentAttachedFilesList.scss';

interface NormalizedAttachment {
  key: string | number | undefined;
  name: string;
  url?: string;
  raw: CommentAttachment;
}

const getExtensionLabel = (name: string): string => {
  if (!name || typeof name !== 'string') return 'FILE';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toUpperCase() : 'FILE';
};

const isImageAttachment = (att: CommentAttachment): boolean => {
  const name = att.filename || att.name || '';
  if (att.mime_type && String(att.mime_type).startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name);
};

const isPdfAttachment = (att: CommentAttachment): boolean => {
  const name = att.filename || att.name || '';
  if (att.mime_type === 'application/pdf') return true;
  return /\.pdf$/i.test(name);
};

const normalize = (att: CommentAttachment): NormalizedAttachment => ({
  key: att.uuid || att.id || att.url || att.file,
  name: att.filename || att.name || 'File',
  url: att.url || att.file || att.link,
  raw: att,
});

const handleActivate = (item: NormalizedAttachment, event: React.MouseEvent<HTMLAnchorElement>): void => {
  const { url, name, raw } = item;
  if (!url) return;

  const image = isImageAttachment(raw);
  const pdf = isPdfAttachment(raw);

  if (image || pdf) {
    event.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  event.preventDefault();
  const a = document.createElement('a');
  a.href = url;
  a.download = name || 'download';
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

interface TimelineCommentAttachmentsProps {
  attachments?: CommentAttachment[];
}

/**
 * Read-only tile list for timeline comment attachments (same look as comment form attachments, no remove).
 * Images and PDF open in a new tab; other types trigger download.
 */
export const TimelineCommentAttachments = ({ attachments }: TimelineCommentAttachmentsProps) => {
  if (!attachments?.length) return null;

  const items = attachments.map(normalize).filter(item => item.url);

  if (!items.length) return null;

  return (
    <ul className="comment-attached-files comment-attached-files--readonly">
      {items.map(item => {
        const { key, name, url, raw } = item;
        const showImage = isImageAttachment(raw) && url;

        return (
          <li key={key} className="comment-attached-files__item" title={name}>
            <a href={url} className="comment-attached-files__link" onClick={e => handleActivate(item, e)}>
              {showImage ? (
                <div className={cn('comment-attached-files__preview', 'comment-attached-files__preview--image')}>
                  <img src={url} alt="" className="comment-attached-files__thumb" />
                </div>
              ) : (
                <div className={cn('comment-attached-files__preview', 'comment-attached-files__preview--file')}>
                  <span className="comment-attached-files__ext">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <path
                        d="M5 14V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H19C19.1314 2.9999 19.2615 3.02568 19.3829 3.07586C19.5042 3.12605 19.6146 3.19967 19.7075 3.2925L26.7075 10.2925C26.8003 10.3854 26.8739 10.4958 26.9241 10.6172C26.9743 10.7385 27.0001 10.8686 27 11V14C27 14.2652 26.8946 14.5196 26.7071 14.7071C26.5196 14.8946 26.2652 15 26 15C25.7348 15 25.4804 14.8946 25.2929 14.7071C25.1054 14.5196 25 14.2652 25 14V12H19C18.7348 12 18.4804 11.8946 18.2929 11.7071C18.1054 11.5196 18 11.2652 18 11V5H7V14C7 14.2652 6.89464 14.5196 6.70711 14.7071C6.51957 14.8946 6.26522 15 6 15C5.73478 15 5.48043 14.8946 5.29289 14.7071C5.10536 14.5196 5 14.2652 5 14ZM20 10H23.5863L20 6.41375V10Z"
                        fill="#717C8C"
                      />
                    </svg>
                    <span>{getExtensionLabel(name)}</span>
                  </span>
                </div>
              )}
            </a>
          </li>
        );
      })}
    </ul>
  );
};
